/**
 * services/web-ui/src/api/voice-sessions.ts
 *
 * Typed API client for the W4-A voice sessions endpoints.
 * Translates the backend's flat audit_log row shape into the clean interfaces
 * used by components.
 *
 * Backend response shapes (actual, from voice-sessions.ts):
 *   GET /api/voice/sessions/:id
 *     { session: {...flat row}, workflow_definition, anomaly_count, all_rules_satisfied,
 *       stages_skipped, policy_denied_count }
 *
 *   GET /api/voice/sessions/:id/events
 *     { events: [{id, entry_id, ts_ms, agent_id, action, tool, result_status,
 *                  duration_ms, details}], count, has_more }
 *
 *   GET /api/voice/sessions/:id/events/poll
 *     { events: [...same...], last_ts }
 */

import type { WorkflowDefinition } from "@vault/types";

const BASE_URL = "/api";

class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const url = `${BASE_URL}${path}`;
  const res = await fetch(url, {
    ...options,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

  if (res.status === 401) throw new ApiError(401, "Unauthorized");
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    throw new ApiError(res.status, (body as { error?: string }).error ?? "Request failed");
  }
  return res.json();
}

// ─── Public interfaces (canonical shapes used by components) ──────────────────

/**
 * A single chronological event in the call replay.
 * Maps from the backend audit_log row.
 */
export interface SessionEvent {
  /** Monotonic row id — used as poll cursor key */
  id: number;
  ts_ms: number;
  /** Always "audit" for now — signal_log not yet persisted */
  source: "audit" | "signal";
  /** The audit_log.action field (e.g. "stage.entered", "policy.denied", "tool.call") */
  type: string;
  payload: Record<string, unknown>;
  /** From details.stage when present (stage.entered carries this) */
  stage?: string;
  agent_id?: string;
  tool?: string | null;
  result_status?: string;
  duration_ms?: number | null;
}

/** Nested recording info, derived from flat voice_sessions columns */
export interface SessionRecording {
  object_key: string;
  bytes: number | null;
  consent: string | null;
}

/** Nested recap info */
export interface SessionRecap {
  text: string;
  sent_at: string;
  channel: string;
}

/** Anomaly derived from backend data */
export interface SessionAnomaly {
  type: string;
  message: string;
  ts: number;
}

/** Full session detail as returned from fetchSession() */
export interface SessionDetail {
  session_id: string;
  direction: string;
  channel: string | null;
  call_sid: string | null;
  caller_number: string | null;
  callee_number: string | null;
  started_at: string;
  ended_at: string | null;
  duration_seconds: number | null;
  status: string;
  task_outcome: string | null;
  workflow_id: string | null;
  workflow_version: number | null;
  workflow_definition: WorkflowDefinition | null;
  current_stage: string | null;
  stages_visited: string[];
  stages_skipped: string[];
  recording: SessionRecording | null;
  recap: SessionRecap | null;
  /** Derived anomaly list (skipped stages + policy denied count) */
  anomalies: SessionAnomaly[];
  all_rules_satisfied: boolean;
  transcript_summary: string | null;
}

/** Response from fetchEvents */
export interface FetchEventsResult {
  events: SessionEvent[];
  last_ts_ms: number;
  has_more: boolean;
}

/** Response from pollEvents */
export interface PollEventsResult {
  events: SessionEvent[];
  last_ts_ms: number;
}

// ─── Raw backend types ────────────────────────────────────────────────────────

interface RawAuditEvent {
  id: number;
  entry_id: string;
  ts_ms: number;
  agent_id: string;
  agent_zone: string;
  action: string;
  tool: string | null;
  topic: string | null;
  result_status: string;
  duration_ms: number | null;
  details: Record<string, unknown>;
}

interface RawSession {
  id: string;
  direction: string;
  channel: string | null;
  call_sid: string | null;
  caller_number: string | null;
  callee_number: string | null;
  started_at: string;
  ended_at: string | null;
  duration_seconds: number | null;
  status: string;
  task_outcome: string | null;
  workflow_id: string | null;
  workflow_version: number | null;
  current_stage: string | null;
  stages_visited: string[];
  stages_skipped: string[];
  recording_object_key: string | null;
  recording_consent: string | null;
  recap_text: string | null;
  recap_sent_at: string | null;
  transcript_summary: string | null;
}

interface RawSessionDetailResponse {
  session: RawSession;
  workflow_definition: WorkflowDefinition | null;
  anomaly_count: number;
  all_rules_satisfied: boolean;
  stages_skipped: string[];
  policy_denied_count: number;
}

interface RawEventsResponse {
  events: RawAuditEvent[];
  count: number;
  has_more: boolean;
}

interface RawPollResponse {
  events: RawAuditEvent[];
  last_ts: number;
}

// ─── Transformers ─────────────────────────────────────────────────────────────

function transformEvent(raw: RawAuditEvent): SessionEvent {
  return {
    id: raw.id,
    ts_ms: raw.ts_ms,
    source: "audit" as const,
    type: raw.action,
    payload: raw.details ?? {},
    // stage.entered carries details.stage; other events may carry it too
    stage: typeof raw.details?.["stage"] === "string"
      ? (raw.details["stage"] as string)
      : typeof raw.details?.["callId"] === "string"
        ? undefined
        : undefined,
    agent_id: raw.agent_id,
    tool: raw.tool,
    result_status: raw.result_status,
    duration_ms: raw.duration_ms,
  };
}

function buildAnomalies(session: RawSession, policyDeniedCount: number): SessionAnomaly[] {
  const anomalies: SessionAnomaly[] = [];

  // One anomaly per skipped stage
  for (const s of session.stages_skipped ?? []) {
    anomalies.push({
      type: "stage.skipped",
      message: `Required stage skipped: ${s}`,
      ts: 0, // no ts available from session row alone
    });
  }

  // Summarize policy denials as a single anomaly if any
  if (policyDeniedCount > 0) {
    anomalies.push({
      type: "policy.denied",
      message: `${policyDeniedCount} policy violation${policyDeniedCount === 1 ? "" : "s"} during call`,
      ts: 0,
    });
  }

  return anomalies;
}

// ─── Public API functions ─────────────────────────────────────────────────────

/** Fetch full detail for a single voice session */
export async function fetchSession(id: string): Promise<SessionDetail> {
  const raw = await request<RawSessionDetailResponse>(`/voice/sessions/${encodeURIComponent(id)}`);
  const s = raw.session;

  return {
    session_id: s.id,
    direction: s.direction,
    channel: s.channel,
    call_sid: s.call_sid,
    caller_number: s.caller_number,
    callee_number: s.callee_number,
    started_at: s.started_at,
    ended_at: s.ended_at,
    duration_seconds: s.duration_seconds,
    status: s.status,
    task_outcome: s.task_outcome,
    workflow_id: s.workflow_id,
    workflow_version: s.workflow_version,
    workflow_definition: raw.workflow_definition,
    current_stage: s.current_stage,
    stages_visited: s.stages_visited ?? [],
    stages_skipped: raw.stages_skipped ?? s.stages_skipped ?? [],
    recording: s.recording_object_key
      ? { object_key: s.recording_object_key, bytes: null, consent: s.recording_consent }
      : null,
    recap: s.recap_text
      ? { text: s.recap_text, sent_at: s.recap_sent_at ?? "", channel: "sms" }
      : null,
    anomalies: buildAnomalies(s, raw.policy_denied_count),
    all_rules_satisfied: raw.all_rules_satisfied,
    transcript_summary: s.transcript_summary,
  };
}

/** Fetch all events for a session (initial load) */
export async function fetchEvents(
  id: string,
  sinceTsMs?: number,
): Promise<FetchEventsResult> {
  const qs = sinceTsMs ? `?since_ts=${sinceTsMs}` : "";
  const raw = await request<RawEventsResponse>(`/voice/sessions/${encodeURIComponent(id)}/events${qs}`);

  const events = raw.events.map(transformEvent);
  const lastTsMs = events.length > 0 ? events[events.length - 1]!.ts_ms : (sinceTsMs ?? 0);

  return {
    events,
    last_ts_ms: lastTsMs,
    has_more: raw.has_more,
  };
}

/** Poll for new events since last cursor (for live calls) */
export async function pollEvents(
  id: string,
  sinceTsMs: number,
): Promise<PollEventsResult> {
  const raw = await request<RawPollResponse>(
    `/voice/sessions/${encodeURIComponent(id)}/events/poll?since_ts=${sinceTsMs}`,
  );

  return {
    events: raw.events.map(transformEvent),
    last_ts_ms: raw.last_ts ?? sinceTsMs,
  };
}

/** Summary row returned from listSessions */
export interface SessionSummary {
  id: string;
  direction: string;
  channel: string | null;
  caller_number: string | null;
  callee_number: string | null;
  status: string;
  started_at: string;
  ended_at: string | null;
  duration_seconds: number | null;
  task_outcome: string | null;
  current_stage: string | null;
  workflow_id: string | null;
}

export interface ListSessionsResult {
  sessions: SessionSummary[];
  total: number;
  limit: number;
  offset: number;
}

/** Paginated list of sessions */
export async function listSessions(params?: {
  limit?: number;
  offset?: number;
  status?: string;
}): Promise<ListSessionsResult> {
  const qs = new URLSearchParams();
  if (params?.limit) qs.set("limit", String(params.limit));
  if (params?.offset) qs.set("offset", String(params.offset));
  if (params?.status) qs.set("status", params.status);
  const query = qs.toString();
  return request<ListSessionsResult>(`/voice/sessions${query ? `?${query}` : ""}`);
}
